/* eslint-disable no-console */

import { isCopEnabled } from '../../services/cookieUtils'
import { getCopTokenAuthConfig } from './config'
import { acquireMsalToken, CopTokenError, CopTokenResult } from './msalTokenProvider'
import { hideRedirectLoader, showRedirectLoader } from './redirectLoader'

let alreadyRan = false
let refreshTimerId: number | null = null

// True when the current page is the MSAL OAuth redirect callback landing (e.g. '/saml-test').
const isOnRedirectCallbackPath = (redirectPath: string): boolean => {
  try {
    const current = window.location.pathname.replace(/\.html$/i, '').replace(/\/$/, '')
    const target = redirectPath.replace(/\/$/, '')
    return Boolean(target) && current === target
  } catch {
    return false
  }
}

const isAuthorMode = (): boolean => {
  try {
    const w = window as unknown as { Granite?: { author?: unknown } }
    return Boolean(w.Granite?.author)
  } catch {
    return false
  }
}

const fetchCurrentUserEmail = async (): Promise<string | null> => {
  try {
    const res = await fetch('/libs/granite/security/currentuser.json?_=' + Date.now())
    if (!res.ok) return null
    const data = (await res.json()) as { email?: string; authorizableId?: string;[key: string]: unknown }
    // Try email first, then fall back to authorizableId
    let email: string
    if (typeof data.email === 'string') {
      email = data.email.trim()
    } else if (typeof data.authorizableId === 'string') {
      email = data.authorizableId.trim()
    } else {
      email = ''
    }
    return email || null
  } catch {
    return null
  }
}

const getCookie = (name: string): string | null => {
  try {
    const match = new RegExp('(^| )' + name + '=([^;]+)').exec(document.cookie)
    return match ? decodeURIComponent(match[2]) : null
  } catch {
    return null
  }
}

const parseExpiryMs = (expiryIso: string): number | null => {
  const ms = Date.parse(expiryIso)
  return Number.isFinite(ms) ? ms : null
}

const scheduleTokenRefreshAtExpiry = (cookieName: string, expiryMs: number, debug: boolean): void => {
  if (typeof window === 'undefined') return

  if (refreshTimerId !== null) {
    window.clearTimeout(refreshTimerId)
    refreshTimerId = null
  }

  const delayMs = Math.max(0, expiryMs - Date.now() + 1000)
  refreshTimerId = window.setTimeout(() => {
    refreshTimerId = null
    alreadyRan = false
    triggerCopTokenIfSessionPresent().catch(err => {
      console.warn('[CopTokenAuth] Scheduled refresh trigger failed:', err)
    })
  }, delayMs)

  if (debug) {
    console.info('[CopTokenAuth] Scheduled token refresh from cookie "' + cookieName + '" in ' + delayMs + 'ms.')
  }
}

type CookieArgs = {
  name: string
  value: string
  maxAgeSeconds: number
  path: string
  sameSite: 'Strict' | 'Lax' | 'None'
  secure: boolean
}

const setExpiryCookie = ({ name, value, maxAgeSeconds, path, sameSite, secure }: CookieArgs): void => {
  try {
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:'
    const parts = [
      `${name}=${encodeURIComponent(value)}`,
      `Path=${path}`,
      `Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`,
      `SameSite=${sameSite}`,
    ]
    if (secure || isHttps || sameSite === 'None') parts.push('Secure')
    document.cookie = parts.join('; ')
  } catch (cookieErr) {
    console.warn('[CopTokenAuth] Unable to write cookie "' + name + '":', cookieErr)
  }
}

const logFailure = (loginHint: string | null, err: CopTokenError | Error): void => {
  const e = err as CopTokenError
  const code = e.errorCode || ''
  const desc = e.errorDescription || ''
  const msg = e.message || String(err)

  const summary = desc || msg || 'Unknown error'

  console.warn('%c[CopTokenAuth] Unable to acquire COP access token. Existing page functionality is unaffected.', 'color:#c93;font-weight:bold;')
  console.warn('  loginHint:        ', loginHint ?? '(none — resolved by Entra SSO session)')
  if (code) console.warn('  errorCode:        ', code)
  if (desc) console.warn('  errorDescription: ', desc)
  console.warn('  message:          ', summary)
}

const hasValidUnexpiredCookie = (cookieName: string, debug: boolean): boolean => {
  const existingExpiry = getCookie(cookieName)
  if (!existingExpiry) return false

  const expiryMs = parseExpiryMs(existingExpiry)
  if (expiryMs && expiryMs > Date.now()) {
    scheduleTokenRefreshAtExpiry(cookieName, expiryMs, debug)
    if (debug) {
      console.info('[CopTokenAuth] Token expiry cookie "' + cookieName + '" is still valid — skipping silent token call.')
    }
    return true
  }

  if (debug) {
    console.info('[CopTokenAuth] Token expiry cookie "' + cookieName + '" is expired/invalid — reacquiring token now.')
  }
  return false
}

const storeAccessToken = (cacheLocation: 'localStorage' | 'sessionStorage', accessToken: string): void => {
  try {
    const storage = cacheLocation === 'localStorage' ? window.localStorage : window.sessionStorage
    storage.setItem('cop_access_token', accessToken)
  } catch (storageErr) {
    console.warn('[CopTokenAuth] Unable to write access token to storage:', storageErr)
  }
}

const writeExpiryCookieAndSchedule = (cfg: ReturnType<typeof getCopTokenAuthConfig>, expiresAtMs: number, expiresInSeconds: number): void => {
  const expiryIso = new Date(expiresAtMs).toISOString()
  setExpiryCookie({
    name: cfg.cookieExpiryName,
    value: expiryIso,
    maxAgeSeconds: expiresInSeconds,
    path: cfg.cookiePath,
    sameSite: cfg.cookieSameSite,
    secure: cfg.cookieSecure,
  })
  scheduleTokenRefreshAtExpiry(cfg.cookieExpiryName, expiresAtMs, cfg.debug)
}

const dispatchTokenAcquired = (cfg: ReturnType<typeof getCopTokenAuthConfig>, result: CopTokenResult): void => {
  try {
    document.dispatchEvent(
      new CustomEvent(cfg.eventName, {
        detail: {
          accessToken: result.accessToken,
          expiresAt: result.expiresAt,
          scope: result.scope,
        },
      }),
    )
  } catch (eventErr) {
    console.warn('[CopTokenAuth] Unable to dispatch "' + cfg.eventName + '":', eventErr)
  }
}

const acquireAndPersistToken = async (
  cfg: ReturnType<typeof getCopTokenAuthConfig>,
  loginHint: string | null,
  onCallbackPath: boolean,
): Promise<void> => {
  let result: CopTokenResult
  try {
    result = await acquireMsalToken({ ...cfg, loginHint: loginHint ?? undefined })
  } catch (e) {
    logFailure(loginHint, e as Error)
      ; (window as unknown as Record<string, unknown>).__copLastError = (e as CopTokenError).raw || e
    // No forced logout: MSAL handles interaction-required cases via its own redirect.
    if (onCallbackPath) hideRedirectLoader()
    return
  }

  const maxAge = result.expiresIn || 3600
  storeAccessToken(cfg.cacheLocation, result.accessToken)
  writeExpiryCookieAndSchedule(cfg, result.expiresAt, maxAge)
    ; (window as unknown as Record<string, unknown>).__copAccessToken = result.accessToken
  dispatchTokenAcquired(cfg, result)
}

export const triggerCopTokenIfSessionPresent = async (): Promise<void> => {
  try {
    if (alreadyRan) return
    alreadyRan = true

    if (typeof window === 'undefined') return
    if (isAuthorMode()) return
    // Gate: only acquire a token for COP/PowerPortal members (cop_powerportal_flag cookie).
    if (!isCopEnabled()) return

    const cfg = getCopTokenAuthConfig()

    // Cover the brief OAuth callback landing with the existing spinner overlay
    // instead of a bare page while the redirect response is processed.
    const onCallbackPath = isOnRedirectCallbackPath(cfg.redirectPath)
    if (onCallbackPath) showRedirectLoader()

    if (hasValidUnexpiredCookie(cfg.cookieExpiryName, cfg.debug)) return

    if (cfg.debug) {
      console.info(
        '[CopTokenAuth] No existing token expiry cookie — ' + 'resolving user email then initiating MSAL silent token acquisition…',
      )
    }

    const loginHint = await fetchCurrentUserEmail()
    if (cfg.debug) {
      console.info('[CopTokenAuth] loginHint:', loginHint ?? '(none — Entra will use SSO session)')
    }

    await acquireAndPersistToken(cfg, loginHint, onCallbackPath)
  } catch (error_) {
    console.warn('[CopTokenAuth] Unexpected internal error during token acquisition; ' + 'existing page functionality is unaffected.', error_)
  }
}